// Which of a payer's payment methods is "the one" for a receipt — and what a
// save in the method picker actually MEANS.
//
// ── Why this file exists ─────────────────────────────────────────────────────
// `document_payment_info` is keyed (docid, payment_service, userid), so every
// method a payer has on a receipt is its own row. There is no "preferred" flag,
// and every reader takes `paymentInfo[0]` — the row with the newest
// `updated_at`. `writeDocPaymentInfo` used to stamp `updated_at = now()` on
// EVERY write, so merely ADDING a second method for a payer silently re-crowned
// the receipt's method for every participant (the Checkout "Pay via" pill, the
// /r SSR shell, the payer pickers, the trip payer grouping).
//
// The fix keeps one column but narrows its meaning:
//
//   `updated_at` = when this row was last DESIGNATED as the receipt's
//                  payer + method. A row that was merely OFFERED as an extra
//                  option was never designated, so it carries NO timestamp.
//
// For non-designating writes `writeDocPaymentInfo` does an UPDATE naming only
// `payment_handle` (falling back to an INSERT with no stamp when no row exists),
// so leaving the designation alone is a property of the SQL rather than of
// PostgREST's conflict handling — and an option row can never outrank a
// designated one. `propagatePaymentHandleToPayerDocs` already relied on exactly
// this property for handle corrections — see its CONCURRENCY note in
// lib/supaBase.ts.
//
// ── The ordering ─────────────────────────────────────────────────────────────
// Payer IDENTITY is unchanged: the doc's payer is still the userid on the
// newest row overall. Only the ordering WITHIN that payer's rows is decided
// here, so the blast radius stays off the payer-attribution surfaces.
//
// Three quantified facts about live data shaped these rules (prod, 2026-08-12):
//   • 3033 of 4049 rows have a NULL `updated_at` at all, so ties are the norm,
//     not the exception — the tiebreak has to be total, or `paymentInfo[0]`
//     flaps between reads.
//   • 74 of the 134 (receipt, payer) pairs that already hold multiple methods
//     have no timestamp on ANY of them. Those are exactly the pairs whose
//     "current method" is arbitrary today; decideDesignation pins the incumbent
//     before an option is added so the Venmo-first tiebreak below can't flip a
//     legacy default.
//   • Only 1 doc of 3757 has multiple payers with no timestamps anywhere, and
//     none have a tie at max(updated_at) — payer identity was already
//     effectively deterministic, which is why it's safe to leave alone.
//
// Everything here is pure so it can be simulated without a database.

/** Minimal shape the ordering needs. Extra fields pass through untouched. */
export interface OrderableMethodRow {
	userId?: string | null;
	paymentService?: string | null;
	paymentHandle?: string | null;
	/** `null`/absent = never designated (an added option, or a legacy row). */
	updatedAt?: string | null;
}

const hasHandle = (r: OrderableMethodRow) => !!(r.paymentHandle && /\S/.test(r.paymentHandle));
const isVenmo = (r: OrderableMethodRow) => (r.paymentService || '').toLowerCase() === 'venmo';

/**
 * Rank two of the SAME payer's rows — lower sorts first, i.e. becomes the
 * receipt's method. In priority order:
 *
 *  1. A row with a usable handle beats an empty one. Empty rows are written
 *     deliberately in places (a payer switch to someone with nothing on file;
 *     TripPageContent's clear-the-method writes), and a designated empty row
 *     would otherwise hide every usable option behind "+ Add Payment Method".
 *  2. Designated beats never-designated, newest designation first.
 *  3. Venmo first — matches getUserPaymentInfo's Venmo-first convention, so an
 *     undesignated payer defaults the same way everywhere.
 *  4. Service name, so the result is fully deterministic rather than
 *     dependent on Postgres' physical row order.
 */
export const compareMethodRows = (a: OrderableMethodRow, b: OrderableMethodRow): number => {
	const ah = hasHandle(a), bh = hasHandle(b);
	if (ah !== bh) return ah ? -1 : 1;

	const at = a.updatedAt || '', bt = b.updatedAt || '';
	if (at !== bt) {
		// Designated (non-empty) always beats never-designated (empty).
		if (!at) return 1;
		if (!bt) return -1;
		return at > bt ? -1 : 1;   // newest designation first
	}

	const av = isVenmo(a), bv = isVenmo(b);
	if (av !== bv) return av ? -1 : 1;

	return (a.paymentService || '').localeCompare(b.paymentService || '');
};

/**
 * Reorder a doc's payment rows so `[0]` is the current payer's current METHOD.
 *
 * The payer is taken from the incoming list's first entry — i.e. whatever the
 * caller's `ORDER BY updated_at DESC NULLS LAST` already decided — so payer
 * attribution is bit-for-bit unchanged. Only that payer's own rows are
 * re-ranked; every other payer's rows keep their incoming (recency) order,
 * which is what the payer pickers in Checkout / ReceiptSummaryList walk when
 * they dedupe by userId and keep the first row per person.
 */
export const orderDocPaymentRows = <T extends OrderableMethodRow>(rows: T[]): T[] => {
	if (!Array.isArray(rows) || rows.length < 2) return rows;
	const payerId = rows[0]?.userId;
	if (!payerId) return rows;
	const mine: T[] = [];
	const others: T[] = [];
	for (const r of rows) (r?.userId === payerId ? mine : others).push(r);
	if (mine.length < 2) return rows;
	mine.sort(compareMethodRows);
	return [...mine, ...others];
};

/**
 * Which of a payer's rows is the incumbent default, by the same ranking. Used
 * by writeDocPaymentInfo to pin a legacy all-untimestamped winner before an
 * option is added next to it.
 */
export const pickDefaultMethodRow = <T extends OrderableMethodRow>(rows: T[]): T | null => {
	const usable = (rows || []).filter(hasHandle);
	if (usable.length === 0) return null;
	return [...usable].sort(compareMethodRows)[0];
};

/**
 * Decide what a non-designating write must do, given every row on the receipt.
 *
 * `updated_at` is compared DOC-WIDE, so stamping it is never a private act: it
 * decides who the receipt's payer is, not just which of one person's methods is
 * current. Both of the cases where an "option" write would still need a stamp
 * are therefore gated on this user actually being the current payer:
 *
 *  • `designate: true`  — nothing usable is on file for them yet, so this method
 *    has to become the receipt's ("unless it's the only method available").
 *  • `pinService`       — they have a usable method that was never designated (a
 *    legacy row; 3 in 4 live rows have no stamp). Its incumbency rests only on
 *    the Venmo-first tiebreak, which a newly added Venmo option would win, so
 *    the incumbent gets stamped in place first.
 *
 * They are NOT always the payer: the divvy editor writes a payee's handle to
 * every trip receipt they hold a ROW on, and on some of those someone else is
 * the payer (a leftover row from a payer switch). Stamping there would silently
 * hand that receipt's attribution to them. When payership can't be established —
 * no row is stamped and more than one person has rows — the answer is
 * `{designate: false, pinService: null}`: write the handle, move nothing.
 */
export const decideDesignation = (
	rows: Array<OrderableMethodRow>,
	userId: string,
): { designate: boolean; pinService: string | null } => {
	const all = rows || [];
	const stamped = all.filter(r => !!r.updatedAt);
	const newest = stamped.length > 0
		? stamped.reduce((a, b) => ((b.updatedAt || '') > (a.updatedAt || '') ? b : a))
		: null;
	// No rows at all → this is the receipt's first method, so it designates.
	const isCurrentPayer = newest
		? newest.userId === userId
		: all.every(r => r.userId === userId);
	if (!isCurrentPayer) return { designate: false, pinService: null };
	const incumbent = pickDefaultMethodRow(all.filter(r => r.userId === userId));
	if (!incumbent) return { designate: true, pinService: null };
	if (incumbent.updatedAt) return { designate: false, pinService: null };
	return { designate: false, pinService: incumbent.paymentService || null };
};

// ── What a save in the picker means ─────────────────────────────────────────

export type PaymentSaveIntent =
	/** Tapped a linked row without changing it — "use this one". */
	| 'select'
	/** A service the payer had no handle for — "here's another option". */
	| 'add'
	/** Same service, different handle — "that handle was wrong". */
	| 'correct';

/**
 * May this viewer change WHICH method the receipt uses — the one every
 * participant sees and pays through?
 *
 * Adding a method and correcting a handle never move it, so this only gates a
 * 'select'. Three cases say yes:
 *
 *  • The viewer IS the payer. It's their money; how they'd like it back is
 *    theirs to state.
 *  • The payer has no account to state it themselves — a `manual:` ("Not on
 *    Tabster?") entry, or a guest/anonymous payer with no public_users profile.
 *    Somebody has to curate their methods, and it's whoever is on the receipt.
 *  • Nothing usable is on file yet, so there's no incumbent to displace.
 *
 * Everyone else picks a rail for THEMSELVES (lib/paymentMethodPick) — their
 * choice partly reflects what they have available to pay with, which is not a
 * claim about the payer.
 *
 * `payerHasProfile: undefined` reads as "has an account" (the conservative
 * answer) so an entry served from a cache written before that field existed
 * can't hand out designation rights it shouldn't.
 */
export const canDesignateReceiptMethod = (args: {
	viewerUserId?: string | null;
	payerUserId?: string | null;
	payerHasProfile?: boolean;
	/** Does the payer already have at least one method with a real handle? */
	hasUsableMethod: boolean;
}): boolean => {
	const { viewerUserId, payerUserId, payerHasProfile, hasUsableMethod } = args;
	if (!hasUsableMethod) return true;
	if (!payerUserId) return true;
	if (viewerUserId && viewerUserId === payerUserId) return true;
	if (payerUserId.startsWith('manual:')) return true;
	return payerHasProfile === false;
};

/**
 * Derive the intent from the values alone, rather than tracking which affordance
 * opened the form. That matters because the form can be entered three ways (no
 * linked methods at all, the "+ Add another method" link, a row's pencil) and
 * the service dropdown can then be switched to a service that IS already
 * linked — at which point an "add" is really a correction. Comparing values at
 * save time gets every path right with no state to keep in sync.
 *
 * Handles are compared the way the payment layer treats them as equal: trimmed,
 * case-insensitive, leading "@" ignored (the popup renders "@handle" and the
 * input strips the prefix, so the same handle round-trips both ways).
 */
export const derivePaymentSaveIntent = (
	serviceKey: string,
	handle: string,
	linkedHandles: Record<string, string> | null | undefined,
): PaymentSaveIntent => {
	const norm = (h: string | null | undefined) => (h || '').trim().replace(/^@/, '').toLowerCase();
	const linked = norm(linkedHandles?.[serviceKey]);
	if (!linked) return 'add';
	return norm(handle) === linked ? 'select' : 'correct';
};
